import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { PaginationActions, PaginationState } from '@/hooks/usePagination';
import type { FilterRecord } from '@/services/types/pagination-types';

export interface MetricBoxConfig {
  id: string;
  label: string;
  count: number;
  tooltip: string;
  filters: Partial<FilterRecord>;
  isClearAll?: boolean;
  icon?: string;
  isClickable?: boolean;
}

interface MetricsBoxesProps {
  boxes: MetricBoxConfig[];
  paginationState: PaginationState;
  paginationActions: PaginationActions;
  metricFilterKeys: string[];
  size?: 'default' | 'compact';
  /** When true, show shimmer placeholders instead of count values */
  loading?: boolean;
}

/**
 * Reusable metrics boxes component for displaying filterable counts.
 * Each box acts as a filter - clicking it updates the URL params.
 * Used by Intakes, Matters, and Tasks pages.
 */
export const MetricsBoxes = ({
  boxes,
  paginationState,
  paginationActions,
  metricFilterKeys,
  size = 'compact',
  loading = false,
}: MetricsBoxesProps) => {
  // Check if a box is active by exact matching its filters to current state
  const isBoxActive = (boxFilters: Partial<FilterRecord>): boolean => {
    const currentFilters = paginationState.filters;
    const boxFilterKeys = Object.keys(boxFilters);

    // If no filters defined for this box (e.g., "Total Intakes" or "Total Tasks"),
    // it's active when none of the metric filter keys are present
    if (boxFilterKeys.length === 0) {
      return !metricFilterKeys.some((key) => currentFilters[key] !== undefined);
    }

    // EXACT MATCH: Check that all box filters are present
    const allBoxFiltersPresent = boxFilterKeys.every((key) => {
      // Skip non-filter keys (e.g., ordering) - they don't affect activation
      if (!metricFilterKeys.includes(key)) {
        return true;
      }

      const boxValue = boxFilters[key];
      const currentValue = currentFilters[key];

      // If the filter key is not in current filters, box is not active
      if (currentValue === undefined) {
        return false;
      }

      // Handle array comparison (for multi-value filters like status)
      if (Array.isArray(boxValue) && Array.isArray(currentValue)) {
        return (
          boxValue.length === currentValue.length &&
          boxValue.every((v) => currentValue.includes(v))
        );
      }

      // Handle array vs string comparison
      if (Array.isArray(boxValue) && !Array.isArray(currentValue)) {
        return boxValue.length === 1 && boxValue[0] === currentValue;
      }
      if (!Array.isArray(boxValue) && Array.isArray(currentValue)) {
        return currentValue.length === 1 && currentValue[0] === boxValue;
      }

      // Direct comparison
      return boxValue === currentValue;
    });

    // EXACT MATCH: Check that NO extra metric filters are present beyond what box defines
    const noExtraMetricFilters = metricFilterKeys.every(
      (key) => boxFilterKeys.includes(key) || currentFilters[key] === undefined,
    );

    return allBoxFiltersPresent && noExtraMetricFilters;
  };

  const handleBoxClick = (box: MetricBoxConfig) => {
    const currentFilters = { ...paginationState.filters };

    if (box.isClearAll) {
      // Clear all metric filter keys but preserve other filters (firm, creator, canonical_groups, etc.)
      metricFilterKeys.forEach((key) => {
        delete currentFilters[key];
      });
      paginationActions.setFilters(currentFilters);
    } else {
      // Clear all other metric filter keys before applying new filter
      // This ensures metric boxes are mutually exclusive
      metricFilterKeys.forEach((key) => {
        delete currentFilters[key];
      });
      const newFilters = { ...currentFilters, ...box.filters };
      paginationActions.setFilters(newFilters as FilterRecord);
    }
  };

  // Size-specific styles following Tailwind spacing scale
  const sizeStyles = {
    default: {
      padding: 'px-4 py-3',
      minWidth: 'min-w-32', // 128px
      labelText: 'text-xs', // 12px
      labelIcon: 'text-sm', // 14px
      countText: 'text-lg', // 18px
      gap: 'gap-1.5', // 6px
      countMargin: 'mt-1', // 4px
    },
    compact: {
      padding: 'px-3 py-2',
      minWidth: 'min-w-28', // 112px
      labelText: 'text-xs', // 12px
      labelIcon: 'text-sm', // 14px
      countText: 'text-base', // 16px
      gap: 'gap-1', // 4px
      countMargin: 'mt-0.5', // 2px
    },
  };

  const styles = sizeStyles[size];

  return (
    <div className="flex border border-gray-300 dark:border-zinc-700 rounded-lg overflow-hidden">
      {boxes.map((box, index) => {
        const isActive = isBoxActive(box.filters);
        const isLastBox = index === boxes.length - 1;

        const isClickable = box.isClickable !== false; // Default to true

        return (
          <Tooltip key={box.id}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => isClickable && handleBoxClick(box)}
                disabled={!isClickable}
                className={`
                  ${isActive ? 'bg-indigo-50 dark:bg-indigo-950' : 'bg-white dark:bg-zinc-900'}
                  ${styles.padding}
                  ${styles.minWidth}
                  transition-colors duration-200
                  ${isClickable ? 'hover:bg-gray-50 dark:hover:bg-zinc-800 cursor-pointer' : 'cursor-default'}
                  focus:outline-none focus-visible:outline-none focus-visible:ring-0
                  ${!isLastBox ? 'border-r border-gray-300 dark:border-zinc-700' : ''}
                  disabled:opacity-100
                `.trim()}
              >
                <div className="text-left">
                  <div
                    className={`flex items-center ${styles.gap} ${styles.labelText} font-medium text-gray-500 dark:text-zinc-400 leading-tight`}
                  >
                    {box.icon && (
                      <span className={styles.labelIcon}>{box.icon}</span>
                    )}
                    <span>{box.label}</span>
                  </div>
                  <div
                    className={`${styles.countText} font-semibold text-black dark:text-white leading-tight ${styles.countMargin}`}
                  >
                    {loading ? (
                      <span className="inline-block w-6 h-4 rounded bg-gray-200 dark:bg-zinc-700 animate-pulse" />
                    ) : (
                      box.count
                    )}
                  </div>
                </div>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-sm">
              <p className="text-xs">{box.tooltip}</p>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
};
